/**
 * POS-PRODUCT-STATS-UX-001D — Estadísticas de Ventas
 * Top 10 automático + autocomplete de catálogo (productoId).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaBoxOpen, FaDownload, FaFilter, FaSearch, FaCalendarAlt, FaTrophy, FaTimes
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import reportesService from '../../services/reportes.service';
import productosService from '../../services/productos.service';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import {
  mergeProductosVentasGanancias,
  resumenProductos,
  ordenarProductos,
  csvProductos,
  descargarCsv,
  resolverFilasReporteVentas,
  resolverFilasReporteGanancias,
  topProductosPorUnidades,
  sugerirProductosCatalogo
} from '../../utils/estadisticasProducto';

function toYmd(d) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function money(n) {
  return (Number(n) || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
}

const PERIODS = [
  { key: 'hoy', label: 'Hoy' },
  { key: 'ayer', label: 'Ayer' },
  { key: 'semana', label: 'Última Semana' },
  { key: 'mes', label: 'Último Mes' },
  { key: 'trimestre', label: 'Último Trimestre' },
  { key: 'anio', label: 'Último Año' }
];

const ReporteEstadisticasProducto = () => {
  const { sucursalSeleccionada } = useAuth();
  const [fechaInicio, setFechaInicio] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return toYmd(d);
  });
  const [fechaFin, setFechaFin] = useState(() => toYmd(new Date()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [incluirFinanzas, setIncluirFinanzas] = useState(false);
  const [query, setQuery] = useState('');
  const [sugerencias, setSugerencias] = useState([]);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  const [indiceActivo, setIndiceActivo] = useState(-1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  const cargar = useCallback(async (inicio = fechaInicio, fin = fechaFin) => {
    if (!inicio || !fin) {
      toast.error('Seleccioná un rango de fechas válido');
      return;
    }
    if (new Date(inicio) > new Date(fin)) {
      toast.error('La fecha de inicio no puede ser mayor a la fecha de fin');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const params = { fechaInicio: inicio, fechaFin: fin };
      if (sucursalSeleccionada?.id) params.sucursal_id = sucursalSeleccionada.id;
      const ventas = await reportesService.obtenerReporteVentas(params);
      const ventasRows = resolverFilasReporteVentas(ventas);

      let ganRows = [];
      let finanzasOk = false;
      try {
        const gan = await reportesService.obtenerReporteGanancias(
          inicio,
          fin,
          'dia',
          sucursalSeleccionada?.id || null
        );
        ganRows = resolverFilasReporteGanancias(gan);
        finanzasOk = true;
      } catch (ganErr) {
        const status = ganErr?.response?.status || ganErr?.status;
        finanzasOk = false;
        if (status && status !== 403) {
          finanzasOk = false;
        }
      }

      setIncluirFinanzas(finanzasOk);
      setRows(mergeProductosVentasGanancias(ventasRows, finanzasOk ? ganRows : []));
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e.message || 'No se pudo cargar el reporte';
      setError(msg);
      setRows([]);
      setIncluirFinanzas(false);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [fechaInicio, fechaFin, sucursalSeleccionada?.id]);

  useEffect(() => {
    cargar();
  }, [sucursalSeleccionada?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await productosService.obtenerActivos();
        if (!cancelled) setCatalogo(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setCatalogo([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onDoc = (ev) => {
      if (boxRef.current && !boxRef.current.contains(ev.target)) {
        setMostrarSugerencias(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const aplicarPeriodo = (key) => {
    const hoy = new Date();
    let inicio = new Date(hoy);
    let fin = new Date(hoy);
    if (key === 'ayer') {
      inicio.setDate(hoy.getDate() - 1);
      fin = new Date(inicio);
    } else if (key === 'semana') {
      inicio.setDate(hoy.getDate() - 7);
    } else if (key === 'mes') {
      inicio.setMonth(hoy.getMonth() - 1);
    } else if (key === 'trimestre') {
      inicio.setMonth(hoy.getMonth() - 3);
    } else if (key === 'anio') {
      inicio.setFullYear(hoy.getFullYear() - 1);
    }
    const i = toYmd(inicio);
    const f = toYmd(fin);
    setFechaInicio(i);
    setFechaFin(f);
    cargar(i, f);
  };

  const refrescarSugerencias = useCallback(async (texto) => {
    const trimmed = String(texto || '').trim();
    if (trimmed.length < 2) {
      setSugerencias([]);
      setMostrarSugerencias(false);
      return;
    }
    let fuente = catalogo;
    if (!fuente.length) {
      try {
        fuente = await productosService.buscar(trimmed, sucursalSeleccionada?.id || null);
      } catch {
        fuente = [];
      }
    }
    const hits = sugerirProductosCatalogo(fuente, trimmed, 10);
    setSugerencias(hits);
    setMostrarSugerencias(true);
    setIndiceActivo(hits.length ? 0 : -1);
  }, [catalogo, sucursalSeleccionada?.id]);

  const onQueryChange = (valor) => {
    setQuery(valor);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      refrescarSugerencias(valor);
    }, 250);
  };

  const seleccionarProducto = (item) => {
    if (!item || item.id == null) return;
    setSelectedProduct({ id: item.id, nombre: item.nombre, codigo: item.codigo });
    setQuery(item.nombre || '');
    setMostrarSugerencias(false);
    setSugerencias([]);
  };

  const quitarProducto = () => {
    setSelectedProduct(null);
    setQuery('');
    setSugerencias([]);
    setMostrarSugerencias(false);
  };

  const onKeyDown = (e) => {
    if (!mostrarSugerencias || sugerencias.length === 0) {
      if (e.key === 'Escape') setMostrarSugerencias(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndiceActivo((i) => (i + 1) % sugerencias.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndiceActivo((i) => (i <= 0 ? sugerencias.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = sugerencias[indiceActivo] || sugerencias[0];
      seleccionarProducto(pick);
    } else if (e.key === 'Escape') {
      setMostrarSugerencias(false);
    }
  };

  const top10 = useMemo(() => topProductosPorUnidades(rows, 10), [rows]);
  const seleccionadoRow = useMemo(() => {
    if (!selectedProduct?.id) return null;
    return rows.find((r) => String(r.id) === String(selectedProduct.id)) || null;
  }, [rows, selectedProduct]);

  const visibles = selectedProduct
    ? (seleccionadoRow ? [seleccionadoRow] : [])
    : top10;
  const resumen = useMemo(
    () => resumenProductos(selectedProduct ? visibles : rows),
    [selectedProduct, visibles, rows]
  );

  const exportarCsv = () => {
    const dataset = selectedProduct
      ? (seleccionadoRow ? [seleccionadoRow] : [])
      : ordenarProductos(rows, 'cantidad', 'desc');
    const contenido = csvProductos(dataset, { incluirFinanzas });
    const suffix = selectedProduct ? String(selectedProduct.id) : 'periodo';
    descargarCsv(contenido, `estadisticas_productos_${fechaInicio}_${fechaFin}_${suffix}.csv`);
    toast.success('CSV exportado');
  };

  const thSort = (label, extraClass = '') => (
    <th className={`px-3 py-2 text-left text-xs font-medium text-e0e3-muted uppercase ${extraClass}`}>
      {label}
    </th>
  );

  const mostrarVacioGeneral = !selectedProduct && rows.length === 0;
  const mostrarProductoSinVentas = Boolean(selectedProduct) && !seleccionadoRow;
  const tituloTabla = selectedProduct
    ? `Estadísticas de ${selectedProduct.nombre || 'producto'}`
    : 'Top 10 productos más vendidos';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-e0e3-foreground flex items-center gap-2">
            <FaTrophy className="text-e0e3-accent" />
            Estadísticas de Ventas
          </h1>
          <p className="text-sm text-e0e3-muted mt-1">
            Analizá el rendimiento de tus productos durante el período seleccionado.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/reportes">
            <Button color="secondary" size="sm">Hub reportes</Button>
          </Link>
          <Button color="secondary" icon={<FaSearch />} onClick={() => cargar()} loading={loading}>
            Actualizar
          </Button>
          <Button
            color="success"
            icon={<FaDownload />}
            onClick={exportarCsv}
            disabled={loading || (selectedProduct ? !seleccionadoRow : rows.length === 0)}
          >
            Exportar CSV
          </Button>
        </div>
      </div>

      <Card title="Filtros" icon={<FaFilter />}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-e0e3-muted mb-1">Fecha Desde</label>
            <div className="relative">
              <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-e0e3-muted/80" />
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="nexo-field pl-10 sm:text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-e0e3-muted mb-1">Fecha Hasta</label>
            <div className="relative">
              <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-e0e3-muted/80" />
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                className="nexo-field pl-10 sm:text-sm"
              />
            </div>
          </div>
          <div className="lg:col-span-2" ref={boxRef}>
            <label className="block text-sm font-medium text-e0e3-muted mb-1">Buscar producto</label>
            <div className="relative">
              <input
                type="text"
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                onKeyDown={onKeyDown}
                onFocus={() => {
                  if (query.trim().length >= 2) refrescarSugerencias(query);
                }}
                placeholder="Escribí nombre o código"
                className="nexo-field sm:text-sm pr-9"
                autoComplete="off"
                aria-autocomplete="list"
              />
              {selectedProduct && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-e0e3-muted hover:text-e0e3-foreground"
                  onClick={quitarProducto}
                  title="Quitar producto"
                >
                  <FaTimes />
                </button>
              )}
              {mostrarSugerencias && query.trim().length >= 2 && (
                <ul className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border border-white/10 bg-e0e3-surface shadow-lg">
                  {sugerencias.length === 0 ? (
                    <li className="px-3 py-2 text-sm text-e0e3-muted">
                      {`No encontramos productos que coincidan con "${query.trim()}".`}
                    </li>
                  ) : sugerencias.map((s, idx) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                          idx === indiceActivo ? 'bg-e0e3-accent/15 text-e0e3-foreground' : 'text-e0e3-muted hover:bg-white/5'
                        }`}
                        onMouseEnter={() => setIndiceActivo(idx)}
                        onClick={() => seleccionarProducto(s)}
                      >
                        <span className="truncate font-medium text-e0e3-foreground">{s.nombre}</span>
                        <span className="ml-3 shrink-0 text-xs">{s.codigo || '—'}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {selectedProduct && (
              <button type="button" className="mt-2 text-xs text-e0e3-accent hover:underline" onClick={quitarProducto}>
                × Quitar producto · Ver todos
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {PERIODS.map((p) => (
            <Button key={p.key} color="secondary" size="sm" onClick={() => aplicarPeriodo(p.key)} disabled={loading}>
              {p.label}
            </Button>
          ))}
          <Button color="primary" size="sm" onClick={() => cargar()} loading={loading} icon={<FaSearch />}>
            Aplicar
          </Button>
        </div>
        {sucursalSeleccionada?.nombre && (
          <p className="text-xs text-e0e3-muted mt-3">
            Sucursal: {sucursalSeleccionada.nombre}. El alcance lo aplica el backend (dataScope).
          </p>
        )}
      </Card>

      {loading ? (
        <Card>
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-e0e3-muted">Cargando estadísticas…</p>
          </div>
        </Card>
      ) : error ? (
        <Card>
          <p className="text-e0e3-danger">{error}</p>
          <Button className="mt-3" color="secondary" onClick={() => cargar()}>Reintentar</Button>
        </Card>
      ) : mostrarVacioGeneral ? (
        <Card>
          <p className="text-e0e3-muted py-8 text-center">
            No hay ventas de productos para el período seleccionado.
          </p>
        </Card>
      ) : mostrarProductoSinVentas ? (
        <Card>
          <p className="text-e0e3-muted py-8 text-center">
            Este producto no tuvo ventas en el período seleccionado.
          </p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Card>
              <p className="text-xs text-e0e3-muted">Unidades vendidas</p>
              <p className="text-xl font-semibold text-e0e3-foreground mt-1">{resumen.unidades}</p>
            </Card>
            <Card>
              <p className="text-xs text-e0e3-muted">Facturación</p>
              <p className="text-xl font-semibold text-e0e3-foreground mt-1">{money(resumen.facturacion)}</p>
            </Card>
            <Card>
              <p className="text-xs text-e0e3-muted">Productos vendidos</p>
              <p className="text-xl font-semibold text-e0e3-foreground mt-1">
                {selectedProduct ? 1 : rows.length}
              </p>
            </Card>
            <Card>
              <p className="text-xs text-e0e3-muted">Más vendido</p>
              <p className="text-sm font-semibold text-e0e3-foreground mt-1">
                {resumen.masVendido?.nombre || '—'}
              </p>
              {resumen.masVendido && (
                <p className="text-xs text-e0e3-muted">{resumen.masVendido.cantidad} u.</p>
              )}
            </Card>
            {incluirFinanzas && (
              <>
                <Card>
                  <p className="text-xs text-e0e3-muted">Ganancia</p>
                  <p className="text-xl font-semibold text-e0e3-foreground mt-1">{money(resumen.ganancia)}</p>
                </Card>
                <Card>
                  <p className="text-xs text-e0e3-muted">Margen</p>
                  <p className="text-xl font-semibold text-e0e3-foreground mt-1">
                    {(Number(resumen.margen) || 0).toFixed(1)}%
                  </p>
                </Card>
              </>
            )}
          </div>

          <Card title={tituloTabla} icon={<FaBoxOpen />}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    {!selectedProduct && thSort('#')}
                    {thSort('Producto')}
                    {thSort('Código', 'hidden md:table-cell')}
                    {thSort('Unidades')}
                    {thSort('Ventas')}
                    {thSort('Participación %', 'hidden md:table-cell')}
                    {incluirFinanzas && thSort('Ganancia', 'hidden lg:table-cell')}
                    {incluirFinanzas && thSort('Margen', 'hidden lg:table-cell')}
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((r, idx) => (
                    <tr key={r.id} className="border-b border-white/5">
                      {!selectedProduct && (
                        <td className="px-3 py-2 text-e0e3-muted">{idx + 1}</td>
                      )}
                      <td className="px-3 py-2 font-medium text-e0e3-foreground">{r.nombre}</td>
                      <td className="px-3 py-2 text-e0e3-muted hidden md:table-cell">{r.codigo || '—'}</td>
                      <td className="px-3 py-2">{r.cantidad}</td>
                      <td className="px-3 py-2">{money(r.total)}</td>
                      <td className="px-3 py-2 hidden md:table-cell">{(Number(r.porcentaje) || 0).toFixed(1)}%</td>
                      {incluirFinanzas && (
                        <td className="px-3 py-2 hidden lg:table-cell">{money(r.ganancia)}</td>
                      )}
                      {incluirFinanzas && (
                        <td className="px-3 py-2 hidden lg:table-cell">
                          {(Number(r.margen) || 0).toFixed(1)}%
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default ReporteEstadisticasProducto;
